import { useMutation } from '@apollo/client';
import { ContractsApi } from '@seek/sbiz-shared-types';
import { getProducts } from '@seek/sbiz-taxonomies';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Routes from 'src/config/routes';
import { tracking } from 'src/tracking';

import CONTRACT_UPGRADE_REQUEST from '../queries/contractUpgradeRequest';

interface UseListingUpgradeContactProps {
  listingId: string;
  clientId: string;
  contractStartDate: string;
}

export interface ListingUpgradeItem {
  badgeType?: string;
  heading: string;
  description: string;
  price: string;
  viewPerformanceStat: string;
  viewPerformanceText: string;
  searchPerformanceStat: string;
  searchPerformanceText: string;
  tooltipText: string;
  productId: string;
}

const upgradeItems: ListingUpgradeItem[] = [
  {
    heading: 'Standard',
    description:
      'Your listing ranks higher in search and is emailed to buyers daily.',
    price: '$949',
    viewPerformanceStat: '2.2x',
    viewPerformanceText: 'ad views',
    searchPerformanceStat: '4.4x',
    searchPerformanceText: 'search views',
    tooltipText:
      'Based on the average performance of Standard listings compared to Free listings during July 2025–June 2026.',
    productId: ContractsApi.Standard,
  },
  {
    badgeType: 'Best performing',
    heading: 'Accelerate',
    description:
      'Your listing gets featured placement in search and buyer emails.',
    price: '$1,299',
    viewPerformanceStat: '4.5x',
    viewPerformanceText: 'ad views',
    searchPerformanceStat: '6.8x',
    searchPerformanceText: 'search views',
    tooltipText:
      'Based on the average performance of Accelerate listings compared to Free listings during July 2025–June 2026.',
    productId: ContractsApi.Accelerate,
  },
];

interface UseListingUpgradeContractItems {
  items: ListingUpgradeItem[];
  hasError: boolean;
  loading: boolean;
  handleUpgradeClick: (productType: string) => Promise<void>;
}

export const useListingUpgradeContractItems = ({
  listingId,
  clientId,
  contractStartDate,
}: UseListingUpgradeContactProps): UseListingUpgradeContractItems => {
  const navigate = useNavigate();
  const [hasError, setHasError] = useState(false);
  const [contractUpgradeRequest, { loading }] = useMutation(
    CONTRACT_UPGRADE_REQUEST,
  );

  const handleUpgradeClick = async (productId: string) => {
    setHasError(false);

    const productLabel =
      getProducts().find(
        (p) => p.productId.toLowerCase() === productId.toLowerCase(),
      )?.title ?? productId;

    tracking.sellerDashboard.upgradeContractRequest.upgrade.clicked({
      tier: productLabel,
      listingId,
      clientId,
      contractStartDate,
    });

    try {
      await contractUpgradeRequest({
        variables: {
          input: {
            listingId,
            productId,
          },
        },
      });
    } catch {
      setHasError(true);
      return;
    }

    const params = new URLSearchParams({
      productType: productLabel,
      listingId,
      clientId,
      contractStartDate,
    });
    navigate(
      `/${Routes.UPGRADE_CONTRACT_REQUEST_CONFIRMATION}?${params.toString()}`,
    );
  };

  return { items: upgradeItems, hasError, loading, handleUpgradeClick };
};
